import { useEffect, useState } from "react";
import { readBlob } from "@/lib/opfs";
import type { Document } from "@/types";

interface DocumentPreviewProps {
  document: Document;
  className?: string;
}

export function DocumentPreview({ document, className = "" }: DocumentPreviewProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!document.opfsPath) return;
    let objectUrl: string | null = null;

    readBlob(document.opfsPath)
      .then((blob) => {
        if (blob) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch(() => setError(true));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document.opfsPath]);

  const isImage = document.mimeType.startsWith("image/");
  const isPdf = document.mimeType === "application/pdf";

  if (error || !url) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 dark:bg-slate-800 ${className}`}>
        <span className="text-xs text-gray-400">No preview</span>
      </div>
    );
  }

  if (isImage) {
    return (
      <img
        src={url}
        alt={document.canonicalName}
        className={`object-contain ${className}`}
        loading="lazy"
      />
    );
  }

  if (isPdf) {
    return (
      <iframe
        src={url}
        title={document.canonicalName}
        className={`border-0 ${className}`}
        loading="lazy"
      />
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-800 ${className}`}>
      <div className="text-2xl font-bold uppercase text-gray-300">{document.extension || "?"}</div>
      <span className="mt-1 text-xs text-gray-400">Preview not available</span>
    </div>
  );
}
